/**
 * Board 3's box and its parts — `docs/job-stages.md` §8, measured from
 * `design/mockups/52-job-stages/html/js-03-popups.html`.
 *
 * **The box is the visit page's `VisitPopup`** (`visit/sheet.tsx`): Board 3
 * draws exactly its shape — rounded 14, `0 14px 40px` at .14, a 14/16 header
 * with a 34px tile beside a 14.5/700 title over its 12px line and a 30px round
 * ✕, a 14/16 body, a 12/16 footer on `surface-alt` with the buttons right —
 * so it is that component at Board 3's widths (480 · 460 · 440), not a second
 * copy of it. **On a phone it is the app's `SheetOrDialog`** — the bottom
 * `PanelSheet` every other popup becomes at 390 — with the same body and the
 * two buttons sharing the row.
 *
 * The parts below are Board 3's own, each a token map of the HTML: the field
 * label (11.5/500 muted, the 5px green dot, *optional* faint at the right),
 * the 30px pill chip, the 52px text box, the green and grey bands, the 36px
 * footer button. Nothing here holds a word: every one comes from `copy.ts`.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { SheetOrDialog } from '../components/sheet-or-dialog.js'
import { VisitPopup } from '../visit/sheet.js'
import { StageGlyph, type StageGlyphName } from './action-glyphs.js'
import { COMMON } from './copy.js'

// ---------------------------------------------------------------------------
// The header's tile
// ---------------------------------------------------------------------------

/** A: accent (✓) · B: info (send) · D: grey (turn back) · E, F: danger (✕). */
export type StageTone = 'accent' | 'info' | 'grey' | 'danger'

const TILE_TONES: Record<StageTone, string> = {
  accent: 'border-accent-line bg-accent-soft text-accent-dark',
  info: 'border-info-line bg-info-bg text-info',
  grey: 'border-line bg-line-soft text-text-muted',
  danger: 'border-danger-line bg-danger-bg text-danger',
}

/** 34 with its 1px border outside, as the board draws it (36 on the page); the glyph 17 at 1.9. */
export function StageTile({ tone, glyph }: { tone: StageTone; glyph: StageGlyphName }) {
  return (
    <span
      className={cn('inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border', TILE_TONES[tone])}
    >
      <StageGlyph name={glyph} size={17} stroke={1.9} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// The footer's buttons
// ---------------------------------------------------------------------------

export type ButtonTone = 'plain' | 'primary' | 'danger'

const BUTTON_TONES: Record<ButtonTone, string> = {
  plain: 'border-line bg-surface text-text hover:bg-hover',
  primary: 'border-accent bg-accent text-white hover:bg-accent-dark',
  danger: 'border-danger-line bg-surface text-danger hover:bg-danger-bg',
}

export interface FooterButton {
  label: string
  tone: ButtonTone
  glyph?: StageGlyphName
  disabled?: boolean
  onClick: () => void
}

/**
 * Board 3's footer button: 36 high, 8 round, 0 14 padding, 12.5/600, a 14.5
 * glyph — or, in a phone's sheet, the sheet's 48px button sharing the row.
 * A primary that cannot be pressed yet keeps its shape and greys, the way the
 * visit page's do; a danger one (*Withdraw*, D) fades.
 */
export function StageButton({ button, phone }: { button: FooterButton; phone: boolean }) {
  return (
    <button
      type="button"
      disabled={button.disabled}
      onClick={button.onClick}
      className={cn(
        // A button does not inherit the page's figures, and the board's do (the visit page's `VisitButton`).
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap border font-semibold tabular-nums',
        phone ? 'h-12 min-w-0 flex-1 rounded-record px-3.5 text-[14.5px]' : 'h-9 rounded-button px-3.5 text-[12.5px]',
        button.disabled && button.tone === 'primary'
          ? 'cursor-not-allowed border-line bg-line-soft text-text-faint'
          : BUTTON_TONES[button.tone],
        button.disabled && button.tone !== 'primary' && 'cursor-not-allowed opacity-50',
      )}
    >
      {button.glyph && <StageGlyph name={button.glyph} size={phone ? 16.5 : 14.5} />}
      {button.label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// The box
// ---------------------------------------------------------------------------

export interface StagePopupProps {
  /** Board 3's width, border included: A 480 · B 460 · D 440 · E 460 · F 440. */
  width: 480 | 460 | 440
  title: string
  sub?: string
  tone: StageTone
  glyph: StageGlyphName
  onClose: () => void
  /** Left to right: the way out (Cancel, Keep it …), then the action. */
  buttons: [FooterButton, FooterButton]
  /** `data-as-drawn` — the board panel (`js-03 A`). */
  asDrawn: string
  /** Forces the phone's sheet, or the desktop box; left out, the window decides. */
  phone?: boolean
  children: ReactNode
}

export function StagePopup({
  width, title, sub, tone, glyph, onClose, buttons, asDrawn, phone: forced, children,
}: StagePopupProps) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const footer = buttons.map((button) => <StageButton key={button.label} button={button} phone={phone} />)

  if (phone) {
    return (
      <SheetOrDialog phone title={title} {...(sub ? { sub } : {})} onClose={onClose} footer={<>{footer}</>}>
        <div data-as-drawn={asDrawn} className="px-4 py-3.5 leading-[normal] tabular-nums text-text">{children}</div>
      </SheetOrDialog>
    )
  }
  return (
    <VisitPopup
      phone={false}
      width={width}
      title={title}
      {...(sub ? { sub } : {})}
      mark={<StageTile tone={tone} glyph={glyph} />}
      onClose={onClose}
      footer={<>{footer}</>}
      asDrawn={asDrawn}
    >
      {children}
    </VisitPopup>
  )
}

// ---------------------------------------------------------------------------
// The body's parts
// ---------------------------------------------------------------------------

/** The body's column: 14 between fields. */
export function Fields({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3.5">{children}</div>
}

/**
 * A field's label: 11.5/500 muted, 5 above its control; the 5px green dot
 * after a required one; *optional* faint at the right of one that is not.
 */
export function FieldLabel({ label, required, optional, htmlFor, id }: {
  label: string
  required?: boolean
  optional?: boolean
  htmlFor?: string
  id?: string
}) {
  return (
    <div className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
      {htmlFor
        ? <label htmlFor={htmlFor} {...(id ? { id } : {})}>{label}</label>
        : <span {...(id ? { id } : {})}>{label}</span>}
      {required && <i aria-hidden="true" className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />}
      {optional && <span className="ml-auto text-text-faint">{COMMON.optional}</span>}
    </div>
  )
}

/**
 * A pill chip — 30 high, 0 10, 12/600; picked, it is the accent pair with a
 * 12px tick at 2.6 (*Handed over*, *I chose someone else*).
 */
export function PillChip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onClick}
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-chip border px-2.5 text-[12px] font-semibold tabular-nums',
        on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover',
      )}
    >
      {on && <StageGlyph name="check" size={12} stroke={2.6} />}
      {label}
    </button>
  )
}

/** A row of chips, one of which may be picked. */
export function ChipRow({ labelledBy, children }: { labelledBy: string; children: ReactNode }) {
  return <div role="radiogroup" aria-labelledby={labelledBy} className="flex flex-wrap gap-1.5">{children}</div>
}

/** The 52px text box (*Why*, *What Karen said*): 10/12, 13 at 1.5, rounded 8. */
export function TextBox({ id, value, onChange, autoFocus, invalid }: {
  id: string
  value: string
  onChange: (value: string) => void
  autoFocus?: boolean
  invalid?: boolean
}) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      autoFocus={autoFocus}
      className={cn('block h-[52px] w-full resize-none rounded-button border bg-surface px-3 py-2.5 text-[13px]',
        'leading-normal text-text outline-none focus:border-accent-line focus:ring-2 focus:ring-accent-ring',
        invalid ? 'border-danger' : 'border-line')}
    />
  )
}

/**
 * The tick box — 18, rounded 5: unticked a 1.5px `line-strong` border,
 * ticked solid accent with a white 12px tick at 3.
 */
export function TickBox({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
        on ? 'bg-accent text-white' : 'border-[1.5px] border-line-strong bg-surface')}
    >
      {on && <StageGlyph name="check" size={12} stroke={3} />}
    </span>
  )
}

/** The radio — 18, a 5px accent ring when picked, 1.5px `line-strong` when not. */
export function RadioDot({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-[18px] w-[18px] shrink-0 rounded-full bg-surface',
        on ? 'border-[5px] border-accent' : 'border-[1.5px] border-line-strong')}
    />
  )
}

/**
 * A band: rounded 10, 10/12, 12.5 at 1.45, a 15px ⓘ, the lead in bold —
 * green on B (*Marks it sent without emailing it.*), grey on D and E.
 */
export function StageBand({ tone, children }: { tone: 'green' | 'grey'; children: ReactNode }) {
  return (
    <div
      role="note"
      className={cn('flex items-start gap-[9px] rounded-card border px-3 py-2.5 text-[12.5px] leading-[1.45]',
        tone === 'green' ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-line-soft text-text-muted')}
    >
      <StageGlyph name="info" size={15} />
      <div>{children}</div>
    </div>
  )
}

/** A refusal from the server, under the body. */
export function Refusal({ message }: { message: string | null }) {
  if (!message) return null
  return <p role="alert" className="m-0 text-[12.5px] text-danger">{message}</p>
}

/** The thrown error's own sentence (the server's), never one written here. */
export const messageOf = (cause: unknown): string => (cause instanceof Error ? cause.message : String(cause))
